import React, { useMemo } from "react";
import { Button, Pagination, PlusIcon } from "@pd/pd-ui-kit";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import Wrapper from "~/shared/ui/Wrapper";
import { useGetPartnersList } from "~/entity/Partners";
import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";
import { usePermissions } from "~/shared/context/PermissionContext";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { sortItems, TableV2, useURLTableSort } from "~/shared/ui/TableV2";
import { DefaultRowControl } from "~/shared/ui/TableV2/ui/DefaultRowControl";

import PartnersFilter from "./PartnersFilter";
import PartnersFormModal from "./PartnersFormModal";
import PartnersViewModal from "./PartnersViewModal";
import { filterPartners } from "./model/filterPartners";
import {
  IPartnersUrlParams,
  parsePartnersFilterFromUrl,
} from "./model/partnersUrlParams";
import { getPartnersColumns } from "./model/tableAdapter";
import { PARTNERS_OPTIONS } from "./options";

import s from "./Partners.less";

const Partners = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const setParams = useURLParamsSetter();
  const urlParams = useURLParamsGetter<IPartnersUrlParams>();
  const {
    page,
    count = 25,
    openModal,
    sortField,
    sortType: sortTypeRaw,
  } = urlParams;

  const sortableColumns = PARTNERS_OPTIONS;

  const { sortType: normalizedSortType, onSortChange } = useURLTableSort({
    sortField: sortField ? String(sortField) : undefined,
    sortType: sortTypeRaw ? String(sortTypeRaw) : undefined,
    sortableColumns,
    setParams,
  });

  const tableSortField =
    sortField && sortableColumns.includes(String(sortField))
      ? String(sortField)
      : undefined;

  const formFilter = useMemo(
    () => parsePartnersFilterFromUrl(urlParams),
    [urlParams],
  );

  const pageNumber = Number(page) || 0;
  const pageSize = Number(count) || 25;

  const { partners, isLoading } = useGetPartnersList({
    page: pageNumber,
    count: pageSize,
    filter: formFilter,
  });

  const openAddModal = () => {
    if (openModal) {
      setParams({ openModal: undefined });
    } else {
      setParams({ openModal: "Create" });
    }
  };

  const filtered = useMemo(
    () => filterPartners(partners, formFilter),
    [partners, formFilter],
  );

  const sorted = useMemo(
    () => sortItems(filtered, tableSortField, normalizedSortType),
    [filtered, tableSortField, normalizedSortType],
  );

  const hasMorePages = partners.length >= pageSize;
  const totalCount = hasMorePages
    ? (pageNumber + 1) * pageSize + 1
    : pageNumber * pageSize + sorted.length;

  const columns = useMemo(
    () =>
      getPartnersColumns(
        t,
        tableSortField,
        normalizedSortType,
        sortableColumns,
      ),
    [t, tableSortField, normalizedSortType, sortableColumns],
  );

  const { handleCount, handlePage } = usePaginationParams("urlParams");

  const goBack = () => history.push("/staff/licenses");

  const { hasPermission } = usePermissions();
  const canEditPartner = hasPermission("employeeAccounting.partners.put");

  const handleRowView = (record: { id?: string | number }) => {
    setParams({ openModal: "View", currentId: String(record.id) });
  };

  const handleRowEdit = (record: { id?: string | number }) => {
    setParams({ openModal: "Edit", currentId: String(record.id) });
  };

  return (
    <Wrapper.Content
      title={t("partners.title")}
      txtBtn={t("ui.back")}
      goBackHandler={goBack}
      controlComponent={
        <Button
          bold
          color="primary"
          fontSize="12"
          icon={<PlusIcon size={16} fill="#ffffff" />}
          onClick={openAddModal}
          radius={5}
          size="medium"
        >
          {t("partners.add")}
        </Button>
      }
      filterComponent={<PartnersFilter />}
    >
      <TableV2
        wrapperClassName={s.tableWrapper}
        columns={columns}
        dataSource={sorted}
        loading={isLoading}
        rowKey="id"
        scroll={{ x: "max-content" }}
        actionsFixed
        actionsPlacement="left"
        sortableColumns={sortableColumns}
        sortField={tableSortField}
        sortType={tableSortField ? normalizedSortType : undefined}
        onSortChange={onSortChange}
        rowActions={(record: { id?: string | number }) => (
          <DefaultRowControl
            record={record}
            onViewDetails={handleRowView}
            onRowEdit={canEditPartner ? handleRowEdit : undefined}
          />
        )}
      />

      <Pagination
        count={pageSize}
        page={pageNumber + 1}
        totalCount={totalCount}
        onChangeCount={handleCount}
        onChangePage={handlePage}
        selectFrom={[25, 50, 100]}
      />

      <PartnersFormModal />
      <PartnersViewModal />
    </Wrapper.Content>
  );
};

export default Partners;
